import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faFilter,
  faSearch,
  faBell,
  faTimes,
  faLightbulb,
  faServer,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/api';
import LoadingScreen from './LoadingScreen';
import { formatTimestamp, formatRelativeTime } from '../utils/dateFormatter';

const Alerts = () => {
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  const [historicalAlerts, setHistoricalAlerts] = useState([]);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);

  useEffect(() => {
    const loadAlerts = async () => {
      try {
        // Use detailed alert templates for both realtime and historical alerts
        const alertTemplates = [
          {
            service: 'Sentinel-2 Ingestion Pipeline',
            severity: 'error',
            message: 'Failed to ingest 23 Sentinel-2 L2A scenes from tile 34SGD',
            details: 'The ingestion pipeline failed due to corrupted metadata in the .SAFE bundle. Band B04 (Red, 665nm) was missing in 23 out of 45 downloaded scenes from orbit 123.\n\nAffected files:\n• S2A_MSIL2A_20240315T092031_N0510_R093_T34SGD_20240315T123456.SAFE\n• S2A_MSIL2A_20240315T092031_N0510_R093_T34TDM_20240315T123457.SAFE\n• ...21 more files\n\nExpected CRS: EPSG:32634 (WGS84 / UTM Zone 34N)\nActual CRS in metadata: Not found\n\nThis is blocking downstream NDVI processing which requires B04 (Red) and B08 (NIR) bands.',
            impact: 'High - NDVI generation pipeline stalled for agricultural monitoring. 23 scenes covering 2,300 km² cannot be processed.',
            affectedResources: ['s3://eo-data/sentinel2/L2A/2024/03/15/T34SGD/', 'NDVI Pipeline Worker #4', 'STAC Item: sentinel2-l2a-34sgd-20240315'],
            aiSuggestions: [
              { text: 'Re-download the affected scenes from Copernicus SciHub backup mirror using: sentinelsat --tile 34SGD --date 20240315 --producttype S2MSI2A', confidence: 'high' },
              { text: 'Check if the issue is specific to orbit R093 or affects other orbits using database query', confidence: 'high' },
              { text: 'Temporarily skip these scenes and process the remaining 22 valid scenes to unblock monitoring', confidence: 'medium' },
              { text: 'Contact ESA support (https://dataspace.copernicus.eu/support) if the issue persists across multiple orbits', confidence: 'low' }
            ]
          },
          {
            service: 'PostGIS Database',
            severity: 'warning',
            message: 'Slow query detected: ST_Intersects on large vector dataset',
            details: 'Query execution time spiked to 14.2 seconds for ST_Intersects operation on the parcels table (2.3M polygons). Missing spatial index on geometry column detected. Query is being used by the GeoPortal crop monitoring dashboard.',
            impact: 'Medium - User-facing dashboard experiencing 10-15s load times',
            affectedResources: ['public.parcels', 'GeoPortal Dashboard API'],
            aiSuggestions: [
              { text: 'Create a GIST spatial index on the geometry column: CREATE INDEX idx_parcels_geom ON parcels USING GIST(geometry)', confidence: 'high' },
              { text: 'Consider partitioning the parcels table by region or season to improve query performance', confidence: 'medium' },
              { text: 'Add a materialized view for frequently queried intersections', confidence: 'medium' },
              { text: 'Review query to use ST_DWithin instead of ST_Intersects if applicable', confidence: 'low' }
            ]
          },
          {
            service: 'GDAL Processing Container',
            severity: 'error',
            message: 'CRS mismatch: EPSG:32634 → EPSG:4326 reprojection failed',
            details: 'Container gdal-processor-2 crashed with OOM (Out of Memory) while attempting to reproject a 45GB Landsat-8 mosaic.\n\nInput file: /data/landsat8/LC08_L2SP_175032_20240315_02_T1_refl.tif\nSource CRS: EPSG:32634 (WGS84 / UTM Zone 34N)\nTarget CRS: EPSG:4326 (WGS84 Geographic)\nFile size: 45.2 GB (12 bands × 7531×7651 pixels)\nContainer RAM: 8GB allocated\nPeak RAM usage: 12.4GB (before crash)\n\nGDAL command executed:\ngdalwarp -s_srs EPSG:32634 -t_srs EPSG:4326 -of GTiff input.tif output.tif\n\nError occurred at: 2024-03-15 14:23:17 UTC\nExit code: 137 (OOM killed by kernel)',
            impact: 'High - Landsat mosaic generation failed for Q1 2024 agricultural analysis. 450,000 hectares of crop monitoring area affected.',
            affectedResources: ['gdal-processor-2 (pod)', '/data/landsat8/LC08_L2SP_175032_20240315_02_T1_refl.tif', 'Processing Pipeline: landsat-atmospheric-correction'],
            aiSuggestions: [
              { text: 'Increase container memory limit to 16GB in kubernetes/deployments/gdal-processor.yaml', confidence: 'high' },
              { text: 'Use GDAL memory constraint: gdalwarp --config GDAL_CACHEMAX 4096 -wm 4096 -s_srs EPSG:32634 -t_srs EPSG:4326 -co TILED=YES -co COMPRESS=LZW input.tif output.tif', confidence: 'high' },
              { text: 'Split the mosaic into 4 tiles using gdal_retile.py and reproject them individually', confidence: 'medium' },
              { text: 'Consider using Cloud Optimized GeoTIFF (COG) format with: -co TILED=YES -co COPY_SRC_OVERVIEWS=YES -co COMPRESS=DEFLATE', confidence: 'medium' }
            ]
          },
          {
            service: 'GeoServer WMS',
            severity: 'warning',
            message: 'Layer rendering timeout for NDVI time-series (500+ requests/min)',
            details: 'GeoServer is experiencing rendering timeouts for the NDVI time-series layer. The layer contains 365 raster files (daily NDVI for 2023), and users are requesting animated visualizations causing 500+ WMS GetMap requests per minute. Average response time: 8.5 seconds.',
            impact: 'Medium - Users reporting slow map loading in web portal',
            affectedResources: ['geoserver-1', 'geoserver-2', 'ndvi_timeseries layer'],
            aiSuggestions: [
              { text: 'Enable GeoWebCache for the NDVI layer with pre-seeded tiles at zoom levels 5-12', confidence: 'high' },
              { text: 'Implement request throttling to limit concurrent WMS requests to 100/min per user', confidence: 'high' },
              { text: 'Consider creating monthly NDVI composites instead of daily to reduce file count', confidence: 'medium' },
              { text: 'Add a CDN in front of GeoServer to cache frequently accessed tiles', confidence: 'low' }
            ]
          },
          {
            service: 'Copernicus SciHub API',
            severity: 'error',
            message: 'API rate limit exceeded - 503 Service Unavailable',
            details: 'The automated ingestion script exceeded Copernicus SciHub API rate limit (2 requests/second). Script attempted 150 requests in 30 seconds while trying to download Sentinel-1 GRD products for flood monitoring. Current retry backoff: 15 minutes.',
            impact: 'High - Flood monitoring data delayed by 2+ hours',
            affectedResources: ['sentinel1-ingestion-cron', 'Flood Monitoring Pipeline'],
            aiSuggestions: [
              { text: 'Implement exponential backoff with jitter in the ingestion script', confidence: 'high' },
              { text: 'Reduce request frequency to 1 request every 2 seconds (0.5 req/s)', confidence: 'high' },
              { text: 'Use the Copernicus Data Space Ecosystem API (no rate limits) instead of SciHub', confidence: 'medium' },
              { text: 'Batch product downloads and schedule them during off-peak hours', confidence: 'low' }
            ]
          }
        ];

        // Generate realtime alerts (3-5 recent ones)
        const realtimeCount = 3 + Math.floor(Math.random() * 3);
        const realtime = Array.from({ length: realtimeCount }, (_, i) => {
          const template = alertTemplates[i % alertTemplates.length];
          const timestamp = new Date(Date.now() - Math.random() * 3600000); // Last hour
          return {
            id: Date.now() + i,
            timestamp: formatTimestamp(timestamp),
            relativeTime: formatRelativeTime(timestamp),
            ...template
          };
        });
        setRealtimeAlerts(realtime);

        const historical = Array.from({ length: 20 }, (_, i) => {
          const template = alertTemplates[i % alertTemplates.length];
          const timestamp = new Date(Date.now() - Math.random() * 86400000 * 7);
          return {
            id: Date.now() + i + 1000,
            timestamp: formatTimestamp(timestamp),
            relativeTime: formatRelativeTime(timestamp),
            ...template,
            resolved: Math.random() < 0.7
          };
        });
        setHistoricalAlerts(historical);

        // Random loading time between 1-2.5 seconds
        const loadingTime = 1000 + Math.random() * 1500;
        setTimeout(() => {
          setLoading(false);
        }, loadingTime);
      } catch (error) {
        console.error('Failed to load alerts:', error);
        setLoading(false);
      }
    };

    loadAlerts();
  }, []);

  // Simulate new alerts occasionally
  useEffect(() => {
    const alertInterval = setInterval(() => {
      if (Math.random() < 0.3) {
        const alertTemplates = [
          {
            service: 'GeoServer',
            severity: 'warning',
            message: 'High memory usage detected',
            details: 'GeoServer memory usage has reached 85% of allocated heap. Multiple concurrent WMS requests are causing memory pressure.',
            impact: 'Medium - Map rendering may slow down',
            affectedResources: ['geoserver-1', 'WMS endpoint'],
            aiSuggestions: [
              { text: 'Increase JVM heap size from 4GB to 6GB', confidence: 'high' },
              { text: 'Enable GeoWebCache to reduce rendering load', confidence: 'high' },
              { text: 'Review and optimize slow WMS layers', confidence: 'medium' }
            ]
          },
          {
            service: 'PostGIS',
            severity: 'error',
            message: 'Database connection pool exhausted',
            details: 'All 50 database connections are in use. New connection requests are being queued.',
            impact: 'High - API requests failing with timeout errors',
            affectedResources: ['PostGIS connection pool', 'API endpoints'],
            aiSuggestions: [
              { text: 'Increase max_connections from 50 to 100 in postgresql.conf', confidence: 'high' },
              { text: 'Identify and terminate long-running queries', confidence: 'high' },
              { text: 'Implement connection pooling with PgBouncer', confidence: 'medium' }
            ]
          },
          {
            service: 'STAC API',
            severity: 'warning',
            message: 'Search query performance degraded',
            details: 'STAC item searches taking 5-8 seconds. Index on datetime field may be missing.',
            impact: 'Medium - Slow catalog searches',
            affectedResources: ['STAC catalog', 'Search endpoint'],
            aiSuggestions: [
              { text: 'Add B-tree index on datetime field', confidence: 'high' },
              { text: 'Enable query result caching for common searches', confidence: 'medium' },
              { text: 'Consider partitioning items table by date', confidence: 'low' }
            ]
          }
        ];

        const template = alertTemplates[Math.floor(Math.random() * alertTemplates.length)];
        const now = new Date();
        const newAlert = {
          id: Date.now(),
          timestamp: formatTimestamp(now),
          relativeTime: formatRelativeTime(now),
          ...template
        };

        setRealtimeAlerts(prev => [newAlert, ...prev.slice(0, 9)]);
      }
    }, 8000);

    return () => clearInterval(alertInterval);
  }, []);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getSeverityBgColor = (severity) => {
    switch (severity) {
      case 'error': return 'rgba(239, 68, 68, 0.1)';
      case 'warning': return 'rgba(245, 158, 11, 0.1)';
      case 'info': return 'rgba(59, 130, 246, 0.1)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  };

  const filteredHistoricalAlerts = historicalAlerts.filter(alert => {
    const matchesSeverity = filterSeverity === 'all' || alert.severity === filterSeverity;
    const matchesSearch = alert.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         alert.message.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSeverity && matchesSearch;
  });

  if (loading) {
    return (
      <div className="alerts-container">
        <LoadingScreen message="Loading system alerts..." />
      </div>
    );
  }

  return (
    <div className="alerts-container">
      <div className="alerts-header">
        <div className="header-title">
          <h2>
            <FontAwesomeIcon icon={faBell} style={{ marginRight: '0.5rem' }} />
            System Alerts
          </h2>
          <div className="alert-summary">
            <span className="alert-count">
              {realtimeAlerts.length} active alerts
            </span>
          </div>
        </div>
      </div>

      <div className="alerts-content">
        {/* Real-time Alerts */}
        <section className="dashboard-section">
          <h3>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
            Active Alerts
            <div className="alert-counter">
              <div className="pulse-indicator"></div>
              <span>{realtimeAlerts.length} active</span>
            </div>
          </h3>
          <div className="alerts-list">
            {realtimeAlerts.length === 0 ? (
              <div className="no-alerts">
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#22c55e', fontSize: '2rem' }} />
                <p>No active alerts - All systems operating normally</p>
              </div>
            ) : (
              realtimeAlerts.map((alert, index) => (
                <div
                  key={alert.id}
                  className={`alert-item ${index === 0 && Date.now() - alert.id < 5000 ? 'new-alert' : ''}`}
                  style={{ backgroundColor: getSeverityBgColor(alert.severity) }}
                  onClick={() => setSelectedAlert(alert)}
                >
                  <div className="alert-header">
                    <span
                      className={`severity ${alert.severity}`}
                    >
                      {alert.severity.toUpperCase()}
                    </span>
                    <span className="timestamp" title={alert.timestamp}>{alert.relativeTime || alert.timestamp}</span>
                    {index === 0 && Date.now() - alert.id < 5000 && (
                      <span className="new-badge">NEW</span>
                    )}
                  </div>
                  <div className="alert-content">
                    <h4>{alert.service}</h4>
                    <p>{alert.message}</p>
                    <p className="alert-click-hint">Click to view AI suggestions</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Historical Alerts */}
        <section className="dashboard-section">
          <h3>
            <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '0.5rem' }} />
            Alert History
          </h3>

          {/* Filters */}
          <div className="alert-filters">
            <div className="filter-group">
              <FontAwesomeIcon icon={faFilter} />
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Severities</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </select>
            </div>
            <div className="search-group">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder="Search alerts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="historical-alerts">
            {filteredHistoricalAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`alert-item historical ${alert.resolved ? 'resolved' : ''}`}
                style={{ backgroundColor: getSeverityBgColor(alert.severity) }}
                onClick={() => setSelectedAlert(alert)}
              >
                <div className="alert-header">
                  <span
                    className={`severity ${alert.severity}`}
                  >
                    {alert.severity.toUpperCase()}
                  </span>
                  <span className="timestamp" title={alert.timestamp}>{alert.relativeTime || alert.timestamp}</span>
                  {alert.resolved && (
                    <span className="resolved-badge">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      RESOLVED
                    </span>
                  )}
                </div>
                <div className="alert-content">
                  <h4>{alert.service}</h4>
                  <p>{alert.message}</p>
                  <p className="alert-click-hint">Click to view AI suggestions</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Alert Details Modal */}
      {selectedAlert && (
        <div className="alert-modal-overlay" onClick={() => setSelectedAlert(null)}>
          <div className="alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="alert-modal-header">
              <div className="modal-title">
                <FontAwesomeIcon icon={faExclamationCircle} style={{ color: getSeverityColor(selectedAlert.severity) }} />
                <h3>Alert Details</h3>
              </div>
              <button className="modal-close" onClick={() => setSelectedAlert(null)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="alert-modal-content">
              <div className="alert-modal-section">
                <div className="modal-severity-badge" style={{ backgroundColor: getSeverityBgColor(selectedAlert.severity), color: getSeverityColor(selectedAlert.severity) }}>
                  {selectedAlert.severity.toUpperCase()}
                </div>
                <h4>{selectedAlert.service}</h4>
                <p className="modal-timestamp">{selectedAlert.timestamp}</p>
              </div>

              <div className="alert-modal-section">
                <h5>
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
                  Issue Description
                </h5>
                <p>{selectedAlert.message}</p>
                {selectedAlert.details && (
                  <p className="modal-details" style={{ whiteSpace: 'pre-wrap' }}>{selectedAlert.details}</p>
                )}
              </div>

              {selectedAlert.impact && (
                <div className="alert-modal-section">
                  <h5>
                    <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: '0.5rem' }} />
                    Impact
                  </h5>
                  <p
                    className="modal-impact"
                    style={{
                      color: selectedAlert.impact.startsWith('High') ? '#ef4444' : '#fbbf24',
                      background: selectedAlert.impact.startsWith('High') ? 'rgba(239, 68, 68, 0.1)' : 'rgba(251, 191, 36, 0.1)',
                      borderLeft: selectedAlert.impact.startsWith('High') ? '3px solid #dc2626' : '3px solid #f59e0b'
                    }}
                  >
                    {selectedAlert.impact}
                  </p>
                </div>
              )}

              {selectedAlert.affectedResources && selectedAlert.affectedResources.length > 0 && (
                <div className="alert-modal-section">
                  <h5>
                    <FontAwesomeIcon icon={faServer} style={{ marginRight: '0.5rem' }} />
                    Affected Resources
                  </h5>
                  <ul className="affected-resources-list">
                    {selectedAlert.affectedResources.map((resource, idx) => (
                      <li key={idx}>
                        <code>{resource}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedAlert.aiSuggestions && selectedAlert.aiSuggestions.length > 0 && (
                <div className="alert-modal-section ai-suggestions-section">
                  <h5>
                    <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '0.5rem', color: '#f59e0b' }} />
                    AI-Powered Suggestions
                  </h5>
                  <div className="ai-suggestions-list">
                    {selectedAlert.aiSuggestions.map((suggestion, idx) => {
                      const suggestionText = typeof suggestion === 'string' ? suggestion : suggestion.text;
                      const confidence = typeof suggestion === 'string' ? 'medium' : suggestion.confidence;
                      const confidenceColors = {
                        high: '#22c55e',
                        medium: '#f59e0b',
                        low: '#6b7280'
                      };
                      const confidenceBgColors = {
                        high: 'rgba(34, 197, 94, 0.1)',
                        medium: 'rgba(245, 158, 11, 0.1)',
                        low: 'rgba(107, 114, 128, 0.1)'
                      };
                      return (
                        <div key={idx} className="ai-suggestion-item">
                          <div className="suggestion-header">
                            <span className="suggestion-number">{idx + 1}</span>
                            <span
                              className="confidence-badge"
                              style={{
                                backgroundColor: confidenceBgColors[confidence],
                                color: confidenceColors[confidence],
                                border: `1px solid ${confidenceColors[confidence]}`
                              }}
                            >
                              {confidence === 'high' && '✓ High confidence (>90%)'}
                              {confidence === 'medium' && '○ Medium (70-90%)'}
                              {confidence === 'low' && '△ Experimental (<70%)'}
                            </span>
                          </div>
                          <p>{suggestionText}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="alert-modal-footer">
              <button className="modal-btn primary" onClick={() => setSelectedAlert(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;